/**
 * Focus Management Utility for Modal Dialogs
 *
 * This utility provides reusable functions to manage focus for accessibility compliance
 * with WCAG 2.4.3 Focus Order requirements.
 */

import { useEffect, useMemo } from 'react'

/**
 * Creates a focus manager object with methods to store and restore focus
 * @returns {Object} Focus manager with store and restore methods
 */
export const createFocusManager = () => {
  let previousActiveElement = null

  return {
    /**
     * Store the currently focused element for later restoration
     */
    storeFocus: () => {
      previousActiveElement = document.activeElement
    },

    /**
     * Restore focus to the previously stored element
     */
    restoreFocus: () => {
      if (
        previousActiveElement &&
        typeof previousActiveElement.focus === 'function'
      ) {
        previousActiveElement.focus()
      }
      previousActiveElement = null
    },

    /**
     * Get the currently stored element (for debugging)
     */
    getStoredElement: () => previousActiveElement,

    /**
     * Clear the stored element without restoring focus
     */
    clearStoredElement: () => {
      previousActiveElement = null
    },
  }
}

export const useFocusManager = (options = {}) => {
  const focusManager = useMemo(() => createFocusManager(options), [])

  useEffect(() => {
    focusManager.storeFocus()
    return () => focusManager.restoreFocus()
  }, [focusManager])

  return focusManager
}
